import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import clsx from "clsx";
import PageTitleBar from "components/PageTitleBar";
import useFeatureTour from "hooks/useFeatureTour";
import { WHATS_NEW_PAGE_KEYS } from "components/WhatsNewMenu/whatsNew.data";
import { CreditCard, MessageCircle, Settings, TrendingUp, Users } from "lucide-react";
import { MenuIntegrations } from "icons/menuIcons";
import AccountSettings from "./components/AccountSettings";
import PlanBilling from "./components/PlanBilling";
import Support from "./components/Support";
import Integrations from "./components/Integrations";
import ManageMembers from "./components/ManageMembers";
import Usage from "./components/Usage";
import { useAppDispatch, useAppSelector } from "store";
import userService from "api/user/user.service";
import { mapUserDetailToRedux } from "api/user/user.type";
import { setAccountSelectedMenu, setUser } from "store/commonSlice";
import { AccountSelectMenu, ACCOUNT_TAB_PARAM, accountMenuFromTabParam } from "./accountMenu";
import { httpStatusOf } from "utils/httpStatusOf";

// Re-exported for the existing `pages/Account/Account` import sites; the enum itself lives in
// `./accountMenu` so `store/commonSlice` can read it without pulling this page in (import cycle).
export { AccountSelectMenu };

// Shared listing-page tab bar (same pill style as Moodboards / Photoshoot Studio).
// Usage is super-admin-only (BE REF-5515 restricted the report to the subscription owner) — only
// surfaced once getUserDetail resolves the caller as the super admin.
const AccountTabs = ({ activeTab, onTabChange, canSeeUsage }: { activeTab: AccountSelectMenu; onTabChange: (tab: AccountSelectMenu) => void; canSeeUsage: boolean | null }) => {
  const { t } = useTranslation();
  const tabs: { id: AccountSelectMenu; label: string; icon: React.ComponentType<{ className?: string }> }[] = [
    { id: AccountSelectMenu.AccountSettings, label: t("account:menu.settings"), icon: Settings },
    { id: AccountSelectMenu.PlanBilling, label: t("account:menu.plan_billing"), icon: CreditCard },
    { id: AccountSelectMenu.ManageMembers, label: t("account:menu.manage_members"), icon: Users },
    ...(canSeeUsage === true ? [{ id: AccountSelectMenu.Usage, label: t("account:menu.usage"), icon: TrendingUp }] : []),
    { id: AccountSelectMenu.Support, label: t("account:menu.support"), icon: MessageCircle },
    { id: AccountSelectMenu.Integrations, label: t("account:menu.integrations"), icon: MenuIntegrations },
  ];

  return (
    <div className="flex items-center gap-1 py-2.5">
      {tabs.map((tab) => (
        <button
          key={tab.id}
          data-testid={`account-tab-${tab.id}`}
          className={clsx(
            "flex items-center gap-1.5 px-3 py-1.5 text-menu rounded transition-colors",
            activeTab === tab.id ? "bg-[#E4E4E4] dark:bg-black-300 text-trueBlack dark:text-white font-medium" : "text-grey hover:text-trueBlack dark:hover:text-white"
          )}
          onClick={() => onTabChange(tab.id)}
        >
          <tab.icon className="w-[16px] h-[16px]" />
          {tab.label}
        </button>
      ))}
    </div>
  );
};

const Account = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  useFeatureTour(WHATS_NEW_PAGE_KEYS.account);
  const { user, accountSelectedMenu } = useAppSelector((state) => state.common);
  const [searchParams] = useSearchParams();
  const [selectedMenu, setSelectedMenu] = useState(accountSelectedMenu);
  // null = still probing. Usage is owner-only: BE REF-6290 moved the gate off the hand-granted
  // `users.is_superadmin` flag onto ownership itself, so the flag we read must be the EFFECTIVE
  // one (`can_manage_billing` = the BE's can_manage_team: staff bypass -> subscription owner ->
  // team_role='Admin' only in an ownerless team). Gating on `user_info.is_superadmin` is what hid
  // the tab from every paying owner who was never granted the flag by hand. Still not the admin
  // roster signal — that grants any team admin and would over-grant while an owner exists.
  // Named for what it holds, not for the flag it used to read: a state called `isSuperAdmin` that
  // actually carries "may manage billing" is the same lie that hid this tab in the first place.
  const [canSeeUsage, setCanSeeUsage] = useState<boolean | null>(null);

  React.useEffect(() => {
    // Stripe add-on checkout returns to /account?addon_success={state_id} — land on the
    // Plan & Billing tab; PlanBilling owns the toast/analytics/param cleanup (REF-5450).
    if (searchParams.has("addon_success")) {
      dispatch(setAccountSelectedMenu(AccountSelectMenu.PlanBilling));
    }
  }, [searchParams, dispatch]);

  React.useEffect(() => {
    // `/account?tab=integrations` (the Miro OAuth return) lands on that tab.
    const fromUrl = accountMenuFromTabParam(searchParams.get(ACCOUNT_TAB_PARAM));
    if (fromUrl !== undefined) dispatch(setAccountSelectedMenu(fromUrl));
  }, [searchParams, dispatch]);

  React.useEffect(() => {
    setSelectedMenu(accountSelectedMenu);
  }, [accountSelectedMenu]);

  React.useEffect(() => {
    if (user.id === "" || user.id === undefined) return;

    userService
      .getUserDetail({
        user_id: user.id,
      })
      .then((res) => {
        // 401/403/409/503 resolve with responseData: null + _handled: true (CLAUDE.md Trap #1).
        if (res?._handled || !res?.responseData) {
          setCanSeeUsage(false);

          return;
        }

        // Usage tab (BE REF-6290) is owner-only — gate on `can_manage_billing`, the effective
        // flag the BE computes per request with the same helper the endpoint's own gate uses, so
        // the tab and the 403 can never disagree. `is_super_admin` (strict owner badge) and
        // `user_info.is_superadmin` (staff flag) are kept as fallbacks: either one implies access
        // under that precedence, and the staff flag must keep working for accounts already granted
        // it as an owner stand-in.
        setCanSeeUsage(res.responseData.can_manage_billing === true || res.responseData.is_super_admin === true || res.responseData.user_info?.is_superadmin === true);
        dispatch(setUser(mapUserDetailToRedux(res.responseData)));
      })
      .catch((error) => {
        // Without this the rejection was unhandled and the owner-only Usage probe never settled.
        // Deny the tab rather than leave a stale grant, and keep the existing Redux user as-is —
        // a failed refresh must not blank out a populated profile.
        // Status only: the error's axios config carries the session bearer token (REF-6708, utils/httpStatusOf).
        console.error("Failed to fetch user details", { status: httpStatusOf(error) });
        setCanSeeUsage(false);
      });
  }, [user.id]);

  const Menu = useMemo(() => {
    switch (selectedMenu) {
      case AccountSelectMenu.AccountSettings:
        return <AccountSettings />;
      case AccountSelectMenu.PlanBilling:
        return <PlanBilling />;
      case AccountSelectMenu.ManageMembers:
        return <ManageMembers />;
      case AccountSelectMenu.Support:
        return <Support />;
      case AccountSelectMenu.Integrations:
        return <Integrations />;
      case AccountSelectMenu.Usage:
        // Guard: a stale Redux accountSelectedMenu must never render the super-admin-only report
        // for a non-owner (or before the probe resolves).
        return canSeeUsage ? <Usage /> : <></>;
      default:
        return <></>;
    }
  }, [selectedMenu, canSeeUsage]);

  return (
    <div className="relative flex-1">
      <div className="flex w-full">
        <div className="flex flex-col w-full">
          <PageTitleBar title={t("account:title")} />
          <div className="border-b border-black-300">
            <div className="px-2.5">
              <AccountTabs
                activeTab={selectedMenu}
                canSeeUsage={canSeeUsage}
                onTabChange={(tab) => {
                  // Keep Redux in sync with the active tab so repeat cross-tab navigation works
                  // (e.g. PlanBilling's "Manage Members" / ManageMembers' "Manage Plan" buttons) —
                  // otherwise re-dispatching the value the tab last set is a no-op and nothing moves.
                  setSelectedMenu(tab);
                  dispatch(setAccountSelectedMenu(tab));
                }}
              />
            </div>
          </div>
          <div className="px-5 py-5">{Menu}</div>
        </div>
      </div>
    </div>
  );
};

export default Account;
